/* eslint-disable no-case-declarations */
import { useTranslation } from "react-i18next";
import { Biometric, BiometricType } from "../../models/entities/biometrics.model";
import { Formatter } from "../../services/formatter/formatter.service";

const TrackHealthSummary = ({ type, data }: { type: BiometricType; data: Biometric[] }) => {
	const { t } = useTranslation("common");

	const getBpValues = () => {
		const systolicData: { date: Date; value: number }[] = [];
		const diastolicData: { date: Date; value: number }[] = [];

		data.forEach((x) => {
			x.entries.forEach((y) => {
				if (y.subType === BiometricType.blood_pressure_systolic) {
					systolicData.push({ date: new Date(x.vitalTime), value: y.value });
				} else if (y.subType === BiometricType.blood_pressure_diastolic) {
					diastolicData.push({ date: new Date(x.vitalTime), value: y.value });
				}
			});
		});
		return {
			systolic: systolicData,
			diastolic: diastolicData,
		};
	};

	const getAverage = () => {
		let result: number | string = 0;

		if (data.length > 0) {
			const values = data.map((x) => x.entries[0].value);
			let min = 0;
			let max = 0;
			let avg = 0;
			switch (type) {
				case BiometricType.blood_pressure:
					// Get all the bp values
					const bpValues = getBpValues();

					// Calculate average: Systolic
					let avgSystolic = null;
					if (bpValues.systolic?.length) {
						avgSystolic = bpValues.systolic.reduce((acc, num) => acc + num.value, 0);
						avgSystolic = Math.round(avgSystolic / bpValues.systolic.length);
					}

					// Calculate average: Diastolic
					let avgDiastolic = null;
					if (bpValues.diastolic?.length) {
						avgDiastolic = bpValues.diastolic.reduce((acc, num) => acc + num.value, 0);
						avgDiastolic = Math.round(avgDiastolic / bpValues.diastolic.length);
					}

					result = `${avgSystolic ?? Formatter.NO_DATA}/${avgDiastolic ?? Formatter.NO_DATA}`;
					break;
				case BiometricType.glucose:
					values.forEach((val) => {
						if (val < min) min = val;
						if (val > max) max = val;
						avg = avg + val;
					});
					result = Math.round(avg / values.length);
					break;
				case BiometricType.weight:
					values.forEach((val) => {
						if (val < min) min = val;
						if (val > max) max = val;
						avg = avg + val;
					});
					result = Math.round((avg / values.length) * 10) / 10;
					break;
			}
		}

		return result;
	};

	const getMin = () => {
		let result: number | string = 0;

		if (data.length > 0) {
			const values = data.map((x) => x.entries[0].value);
			switch (type) {
				case BiometricType.blood_pressure:
					// Get all the bp values
					const bpValues = getBpValues();

					// Get the minimum Systolic reading
					const minSystolicReading =
						bpValues.systolic.length > 0
							? bpValues.systolic.reduce(
								(prev, curr) => (prev.value < curr.value ? prev : curr),
								bpValues.systolic[0] // Initial value
							)
							: null;

					// Get the associated Diastolic value for that reading
					const minDiastolic = minSystolicReading
						? bpValues.diastolic?.find(
							(x) =>
								new Date(x.date).getTime() === minSystolicReading.date.getTime()
						)?.value || Formatter.NO_DATA
						: Formatter.NO_DATA;

					result = minSystolicReading
						? `${minSystolicReading.value ?? Formatter.NO_DATA}/${minDiastolic ?? Formatter.NO_DATA}`
						: Formatter.NO_DATA;
					break;
				case BiometricType.glucose:
				case BiometricType.weight:
					result = Math.min(...values);
					break;
			}
		}

		return result;
	};

	const getMax = () => {
		let result: string | number = 0;

		if (data.length > 0) {
			const values = data.map((x) => x.entries[0].value);
			switch (type) {
				case BiometricType.blood_pressure:
					// Get all the bp values
					const bpValues = getBpValues();

					// Get the maximum Systolic reading
					const maxSystolicReading =
						bpValues.systolic.length > 0
							? bpValues.systolic.reduce(
								(prev, curr) => (prev.value > curr.value ? prev : curr),
								bpValues.systolic[0] // Initial value
							)
							: null;

					// Get the associated Diastolic value for that reading
					const maxDiastolic = maxSystolicReading
						? bpValues.diastolic?.find(
							(x) =>
								new Date(x.date).getTime() === maxSystolicReading.date.getTime()
						)?.value || Formatter.NO_DATA
						: Formatter.NO_DATA;

					result = maxSystolicReading
						? `${maxSystolicReading.value ?? Formatter.NO_DATA}/${maxDiastolic ?? Formatter.NO_DATA}`
						: Formatter.NO_DATA;
					break;
				case BiometricType.glucose:
				case BiometricType.weight:
					result = Math.max(...values);
					break;
			}
		}

		return result;
	};

	return (
		<div className="track-health-summary">
			<ul>
				{/* Minimum value */}
				<li>
					<div className="track-health-summary__title">{t("TRACK_HEALTH.MIN")}</div>
					<div className="track-health-summary__value">{getMin()}</div>
				</li>
				{/* Average value */}
				<li>
					<div className="track-health-summary__title">{t("TRACK_HEALTH.AVG")}</div>
					<div className="track-health-summary__value">{getAverage()}</div>
				</li>
				{/* Maximum value */}
				<li>
					<div className="track-health-summary__title">{t("TRACK_HEALTH.MAX")}</div>
					<div className="track-health-summary__value">{getMax()}</div>
				</li>
			</ul>
		</div>
	);
};

export default TrackHealthSummary;
